import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Section } from "./shared";
import { useFaqs, localizeFaq } from "@/hooks/useFaqs";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";
import FaqSchema from "@/components/seo/FaqSchema";

interface Props {
  solution: SolutionData;
}

const SolutionFAQ = ({ solution }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const { data: faqs = [] } = useFaqs("solution", solution.slug);

  // Fallback to hardcoded FAQs while DB populates
  const raw = faqs.length > 0 ? faqs : (solution.faqs ?? []);
  const items = raw.map((f) => localizeFaq(f as any, lang));

  if (items.length === 0) return null;

  return (
    <Section>
      <div className="max-w-3xl">
        <FaqSchema items={items} />
        <div className="flex items-center gap-3 mb-2">
          <span className="w-6 h-0.5 rounded-full bg-primary" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Planning your next steps", es: "Planifica tus próximos pasos", "zh-cn": "实施规划后续行动" }[lang])}</p>
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-10">{t("faqTitle")}</h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Accordion type="single" collapsible className="w-full">
            {items.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border/50">
                <AccordionTrigger className="text-left text-sm font-medium text-foreground hover:text-primary transition-colors [&[data-state=open]]:text-primary py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </Section>
  );
};

export default SolutionFAQ;
